import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Check, X, Plus, Minus } from "lucide-react";
import { fr } from "../content/fr";
import { IMG } from "../lib/images";
import { fetchAngles } from "../lib/api";
import { Reveal, Marquee } from "./ui-bits";
import PlatformPreview from "./PlatformPreview";
import Img from "./Img";

/* ---------- 01 HERO ---------- */
export function Hero() {
  return (
    <section className="hero" id="top" data-testid="hero-section">
      <div className="hero__bg"><Img src={IMG.heroBg} alt="" width={1264} height={848} eager priority /></div>
      <div className="container hero__inner">
        <div>
          <motion.span className="hero__badge" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--v-accent)" }} /> {fr.hero.badge}
          </motion.span>
          <motion.h1 className="h1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}>
            {fr.hero.title_1} <span className="gradient-text">{fr.hero.title_grad}</span> {fr.hero.title_2}
          </motion.h1>
          <motion.p className="hero__cadrage" data-testid="hero-cadrage" style={{ marginTop: "1rem", fontSize: "0.98rem", lineHeight: 1.6, color: "var(--v-accent-text)", fontWeight: 500, maxWidth: "min(60ch,100%)" }} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            {fr.hero.cadrage}
          </motion.p>
          <motion.p className="lead" style={{ marginTop: "1.4rem" }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
            {fr.hero.subtitle}
          </motion.p>
          <div style={{ borderLeft: "3px solid var(--v-accent)", paddingLeft: "1rem", margin: "1.4rem 0", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {fr.hero.lines.map((l, i) => <p key={i} style={{ color: "var(--text-secondary)", fontSize: "0.95rem" }}>{l}</p>)}
          </div>
          <p className="muted" style={{ fontSize: "0.76rem", marginTop: "-0.6rem", marginBottom: "0.4rem", maxWidth: "min(52ch,100%)" }}>{fr.hero.sourceNote}</p>
          <motion.div className="hero__actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}>
            <Link to="/pilote" className="btn btn--primary" data-testid="hero-cta-primary">{fr.hero.ctaPrimary} <ArrowRight size={16} /></Link>
          </motion.div>
          <p className="hero__note">{fr.hero.note}</p>
        </div>
        <div className="hero__stage" data-testid="hero-visual">
          <motion.div className="hero__stage-inner" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <PlatformPreview />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function BrandMarquee() { return <Marquee items={fr.marquee} />; }

export function Personas() {
  const p = fr.personas;
  return (
    <section className="section" id="influenceurs" data-testid="personas-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{p.eyebrow}</span>
          <h2 className="h2">{p.title}</h2>
          <p className="lead" style={{ marginTop: "1rem" }}>{p.lede}</p>
        </Reveal>
        <div className="persona-grid">
          {p.cards.map((c, i) => (
            <Reveal className="persona-card" key={c.nom} delay={i * 0.06}>
              <div className="persona-media">
                <Img
                  src={c.img}
                  srcSet={c.img.replace(/\.(jpe?g|png)$/i, "-424.webp") + " 424w, " + c.img.replace(/\.(jpe?g|png)$/i, ".webp") + " 848w"}
                  sizes="(max-width: 768px) 50vw, 266px"
                  alt={"Influenceur IA " + c.nom} width={848} height={1264}
                />
                <span className="persona-tag mono">IA</span>
              </div>
              <div className="persona-body">
                <h3>{c.nom}</h3>
                <p className="persona-niche">{c.niche}</p>
                <p className="persona-canal mono">{c.canal}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="glass card persona-caps" delay={0.1}>
          <h3 style={{ marginBottom: "1.1rem", fontSize: "1.1rem" }}>{p.capsTitle}</h3>
          <ul>
            {p.caps.map((x, i) => (<li key={i}><Check size={16} /> <span>{x}</span></li>))}
          </ul>
        </Reveal>

        <Reveal className="team" delay={0.05}>
          <div className="team__media"><Img src={p.team} srcSet={p.team.replace(/\.(jpe?g|png)$/i, "-640.webp") + " 640w, " + p.team.replace(/\.(jpe?g|png)$/i, ".webp") + " 1264w"} sizes="(max-width: 768px) 100vw, 600px" alt="L'équipe d'influenceurs IA VIRALYA" width={1264} height={848} /></div>
          <div className="team__body">
            <span className="eyebrow"><span className="sq" />{p.teamEyebrow}</span>
            <h3 className="h3" style={{ margin: "0.5rem 0 0.75rem" }}>{p.teamTitle}</h3>
            <p style={{ color: "var(--text-secondary)" }}>{p.teamLede}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 02 CREDIBILITY + CONFESSION ---------- */
export function Credibility() {
  const c = fr.credibility;
  return (
    <section className="section" data-testid="credibility-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{c.eyebrow}</span>
          <h2 className="h2">{c.title}</h2>
        </Reveal>
        <div className="grid-4">
          {c.facts.map((f, i) => (
            <Reveal className="glass card" key={f.k} delay={i * 0.05}>
              <h3 className="card__title">{f.k}</h3>
              {f.html ? <p className="card__desc" dangerouslySetInnerHTML={{ __html: f.v }} /> : <p className="card__desc">{f.v}</p>}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 03 PROBLEM ---------- */
export function Problem() {
  const p = fr.problem;
  return (
    <section className="section section--soft" data-testid="problem-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{p.eyebrow}</span>
          <h2 className="h2">{p.title}</h2>
        </Reveal>
        <div className="grid-3">
          {p.days.map((d, i) => (
            <Reveal className="glass card" key={d.j} delay={i * 0.05}>
              <div className="mono" style={{ color: "var(--v-accent-text)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>{d.j}</div>
              <p className="card__desc">{d.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="h3" style={{ fontFamily: "var(--font-head)", marginTop: "2rem", maxWidth: "min(40ch,100%)" }}>{p.punchline}</p></Reveal>
        <Reveal className="glass card" style={{ marginTop: "1.5rem", background: "var(--v-surface-inverse)", color: "var(--v-text-on-inverse)", borderLeft: "4px solid var(--v-accent)" }}>
          <p>{p.enemy}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 04 TIME SAVED ---------- */
export function TimeSaved() {
  const t = fr.timesaved;
  return (
    <section className="section" id="moteur" data-testid="timesaved-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{t.eyebrow}</span>
          <h2 className="h2">{t.title}</h2>
        </Reveal>
        <Reveal className="glass" style={{ overflowX: "auto" }}>
          <table className="data-table" style={{ minWidth: 620 }} data-testid="timesaved-table">
            <thead><tr>{t.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {t.rows.map((r, i) => (
                <tr key={i}><td className="mono">{r[0]}</td><td>{r[1]}</td><td className="tnum">{r[2]}</td><td className="tnum">{r[3]}</td><td className="tnum" style={{ color: "var(--v-success)" }}>{r[4]}</td></tr>
              ))}
              <tr data-testid="timesaved-negative" style={{ background: "rgba(179,38,30,0.06)" }}>
                <td className="mono">{t.negative[0]}</td><td style={{ color: "var(--v-error)" }}>{t.negative[1]}</td><td className="tnum">{t.negative[2]}</td><td className="tnum">{t.negative[3]}</td><td className="tnum" style={{ color: "var(--v-error)" }}>{t.negative[4]}</td>
              </tr>
              <tr style={{ fontWeight: 700 }}><td></td><td>{t.total[0]}</td><td className="tnum">{t.total[1]}</td><td className="tnum">{t.total[2]}</td><td className="tnum" style={{ color: "var(--v-success)" }}>{t.total[3]}</td></tr>
            </tbody>
          </table>
        </Reveal>
        <Reveal><p className="muted" style={{ marginTop: "1rem", fontSize: "0.9rem", maxWidth: "min(70ch,100%)" }}>{t.reading}</p></Reveal>
        <div className="grid-3" style={{ marginTop: "1.5rem" }}>
          {t.conversion.map((c) => (
            <Reveal className="glass card" key={c.v}><div className="gradient-text tnum" style={{ fontFamily: "var(--font-head)", fontSize: "2rem", fontWeight: 700 }}>{c.v}</div><p className="card__desc">{c.l}</p></Reveal>
          ))}
        </div>
        <Reveal><p className="muted" style={{ marginTop: "0.75rem", fontSize: "0.78rem", maxWidth: "min(70ch,100%)" }}>{t.sourceNote}</p></Reveal>
        <Reveal className="glass card" style={{ marginTop: "1.5rem", background: "var(--v-surface-inverse)", color: "var(--v-text-on-inverse)" }}><p>{t.closing}</p></Reveal>
      </div>
    </section>
  );
}

/* ---------- 05 PACTE LOOP ---------- */
export function PacteLoop() {
  const p = fr.pacte;
  return (
    <section className="section section--soft" id="methode" data-testid="pacte-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{p.eyebrow}</span>
          <h2 className="h2">{p.title}</h2>
        </Reveal>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {p.steps.map((s, i) => {
            const isE = s.l === "E";
            return (
              <Reveal className="glass card" key={s.l} delay={i * 0.04} data-testid={`pacte-step-${s.l}`}
                style={isE ? { background: "var(--v-surface-inverse)", color: "var(--v-text-on-inverse)", display: "grid", gridTemplateColumns: "76px 1fr", gap: "1.25rem" } : { display: "grid", gridTemplateColumns: "76px 1fr", gap: "1.25rem" }}>
                <div className="gradient-text" style={{ fontFamily: "var(--font-head)", fontSize: "3rem", fontWeight: 700, lineHeight: 1, color: isE ? "var(--v-accent-bright)" : undefined }}>{s.l}</div>
                <div>
                  <div className="mono" style={{ fontSize: "0.72rem", opacity: 0.8, marginBottom: "0.3rem" }}>{s.timing}</div>
                  <h3 className="card__title">{s.t}</h3>
                  <p style={{ color: isE ? "var(--v-text-on-inverse)" : "var(--text-secondary)", opacity: isE ? 0.9 : 1 }}>{s.d}</p>
                  {isE && <p style={{ fontWeight: 700, marginTop: "0.6rem" }}>{p.stepE_bold}</p>}
                  <p style={{ fontStyle: "italic", marginTop: "0.6rem", paddingTop: "0.6rem", borderTop: "1px dashed var(--v-border)", fontSize: "0.85rem", opacity: 0.75 }}>Ce que tout le monde fait à la place : {s.contre}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal><p className="h3" style={{ fontFamily: "var(--font-head)", marginTop: "1.5rem" }}>{p.closing}</p></Reveal>
      </div>
    </section>
  );
}

/* ---------- 06 PRICING ---------- */
export function Pricing() {
  const p = fr.pricing;
  return (
    <section className="section" id="offre" data-testid="pricing-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{p.eyebrow}</span>
          <h2 className="h2">{p.title}</h2>
        </Reveal>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {p.anchors.map((a) => (
            <Reveal className="glass" key={a.k} style={{ padding: "1.1rem 1.4rem", display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "baseline", flexWrap: "wrap" }}>
              <div><strong>{a.k}</strong><div className="muted" style={{ fontSize: "0.85rem" }}>{a.d}</div></div>
              <div className="tnum" style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 700 }}>{a.v}</div>
            </Reveal>
          ))}
          <Reveal className="glass" style={{ padding: "1.1rem 1.4rem", display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "baseline", flexWrap: "wrap", background: "var(--v-surface-inverse)", color: "var(--v-text-on-inverse)", border: "1px solid var(--v-accent)" }} data-testid="anchor-final">
            <div><strong>{p.anchorFinal.k}</strong><div style={{ fontSize: "0.85rem", opacity: 0.85 }}>{p.anchorFinal.d}</div></div>
            <div className="tnum" style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", fontWeight: 700, color: "var(--v-accent-bright)" }}>{p.anchorFinal.v}</div>
          </Reveal>
        </div>

        <div className="grid-3" style={{ marginTop: "2rem", alignItems: "stretch" }}>
          {p.plans.map((pl) => (
            <Reveal className="glass card" key={pl.nom} style={pl.ref ? { border: "2px solid var(--v-accent)", transform: "translateY(-12px)" } : undefined} data-testid={`plan-${pl.nom}`}>
              {pl.ref && <span className="chip" style={{ marginBottom: "0.8rem" }}>{p.refLabel}</span>}
              <h3 className="h3">{pl.nom}</h3>
              <div className="gradient-text tnum" style={{ fontFamily: "var(--font-head)", fontSize: "1.8rem", fontWeight: 700, margin: "0.4rem 0 1rem" }}>{pl.prix}</div>
              <ul className="offer-list" style={{ margin: 0 }}>
                {pl.detail.map((d) => <li key={d}><Check size={16} /> <span>{d}</span></li>)}
              </ul>
            </Reveal>
          ))}
        </div>

        <div className="grid-2" style={{ marginTop: "1.25rem" }}>
          {p.onRequest.map((o) => (
            <Reveal className="glass card" key={o.nom} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <div><strong>{o.nom}</strong> · <span className="tnum">{o.prix}</span><div className="muted" style={{ fontSize: "0.85rem" }}>{o.d}</div></div>
              <Link to="/audit" className="btn btn--ghost" data-testid={`onrequest-${o.nom}`}>{p.onRequestCta}</Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="glass card" style={{ marginTop: "1.25rem" }}>
          <h3 className="card__title">{p.launch.title}</h3>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", margin: "0.5rem 0" }}>{p.launch.lines.map((l) => <span key={l} className="tnum">{l}</span>)}</div>
          <p className="muted" style={{ fontSize: "0.88rem" }}>{p.launch.note}</p>
        </Reveal>

        <div style={{ marginTop: "2rem" }}>
          <Link to="/pilote" className="btn btn--primary" data-testid="pricing-cta">{p.ctaPilote} <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- 07 PROOF ---------- */
export function Proof() {
  const p = fr.proof;
  return (
    <section className="section section--soft" data-testid="proof-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{p.eyebrow}</span>
          <h2 className="h2">{p.title}</h2>
          <p className="lead" style={{ marginTop: "1rem" }}>{p.lede}</p>
        </Reveal>
        <div className="grid-3">
          {p.characters.map((c, i) => (
            <Reveal className="glass card" key={c.nom} delay={i * 0.05} data-testid={`character-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
                <h3 className="card__title" style={{ margin: 0 }}>{c.nom}</h3>
                {c.ia && <span className="live-badge">IA</span>}
              </div>
              <p className="card__desc">{c.desc}</p>
              <div className="mono muted" style={{ fontSize: "0.78rem", marginTop: "0.6rem" }}>{c.canaux}</div>
              <div className="chip" style={{ marginTop: "0.8rem" }}>{c.badge}</div>
              {c.ia && <div className="mono muted" style={{ fontSize: "0.72rem", marginTop: "0.6rem" }} data-testid={`case-study-badge-${i}`}>{p.caseStudyBadge}</div>}
            </Reveal>
          ))}
        </div>
        <Reveal><p className="muted" style={{ marginTop: "1.25rem", fontSize: "0.88rem", maxWidth: "min(80ch,100%)" }}>{p.rule}</p></Reveal>
        <Reveal className="glass card" style={{ marginTop: "1rem" }}><p style={{ color: "var(--text-secondary)" }}><strong>Défaut connu&nbsp;:</strong> {p.defect}</p></Reveal>
      </div>
    </section>
  );
}

/* ---------- 08 COMPETITIVE ---------- */
export function Competitive() {
  const c = fr.competitive;
  return (
    <section className="section" data-testid="competitive-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{c.eyebrow}</span>
          <h2 className="h2">{c.title}</h2>
        </Reveal>
        <div className="grid-3">
          {c.losses.map((l, i) => (
            <Reveal className="glass card" key={i} delay={i * 0.05}><p style={{ color: "var(--v-success)", fontWeight: 600 }}>{l}</p></Reveal>
          ))}
        </div>
        <Reveal><p className="h3" style={{ fontFamily: "var(--font-head)", marginTop: "1.5rem" }}>{c.closing}</p></Reveal>
      </div>
    </section>
  );
}

/* ---------- 09 GUARANTEE ---------- */
export function Guarantee() {
  const g = fr.guarantee;
  return (
    <section className="section section--soft" data-testid="guarantee-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{g.eyebrow}</span>
          <h2 className="h2">{g.title}</h2>
        </Reveal>
        <div className="offer-wrap" style={{ alignItems: "start" }}>
          <Reveal>
            <div className="grid-3">
              {g.thresholds.map((t) => (
                <div className="glass card center" key={t.l}><div className="gradient-text tnum" style={{ fontFamily: "var(--font-head)", fontSize: "2.2rem", fontWeight: 700 }}>{t.v}</div><p className="card__desc">{t.l}</p></div>
              ))}
            </div>
            <p className="h3" style={{ fontFamily: "var(--font-head)", marginTop: "1.25rem" }}>{g.promise}</p>
          </Reveal>
          <Reveal className="glass card" delay={0.1}>
            <h3 className="card__title">{g.exclusionsTitle}</h3>
            <ul className="offer-list">{g.exclusions.map((e) => <li key={e}><X size={16} style={{ color: "var(--v-error)" }} /> <span>{e}</span></li>)}</ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- 10 FOUNDERS ---------- */
export function Founders({ places = 0 }) {
  const f = fr.founders;
  return (
    <section className="section" data-testid="founders-section">
      <div className="container">
        <Reveal className="final-banner" style={{ textAlign: "left" }}>
          <span className="eyebrow" style={{ color: "var(--v-accent-bright)" }}>{f.eyebrow}</span>
          <h2 className="h2" style={{ color: "var(--v-text-on-inverse)" }}>{f.title}</h2>
          <p className="lead" style={{ color: "var(--v-text-on-inverse)", opacity: 0.86, marginTop: "0.75rem" }}>{f.subtitle}</p>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", margin: "1.5rem 0" }}>
            {f.prices.map((pr) => (
              <div key={pr.nom}><div className="mono" style={{ fontSize: "0.75rem", opacity: 0.8 }}>{pr.nom}</div><div className="tnum" style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 700, color: "var(--v-accent-bright)" }}>{pr.prix}</div></div>
            ))}
          </div>
          <div className="tnum" style={{ fontFamily: "var(--font-head)", fontSize: "2rem", fontWeight: 700 }} data-testid="founders-counter">{places} / 20 <span style={{ fontSize: "0.9rem", fontFamily: "var(--font-mono)", opacity: 0.8 }}>{f.counterLabel}</span></div>
          <p style={{ fontSize: "0.85rem", opacity: 0.8, marginTop: "0.75rem", maxWidth: "min(70ch,100%)" }}>{f.counterNote}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 11 TRANSPARENCY ---------- */
export function TransparencyMatrix() {
  return (
    <section className="section section--soft" data-testid="transparency-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{fr.transparency.eyebrow}</span>
          <h2 className="h2">{fr.transparency.title}</h2>
        </Reveal>
        <Reveal className="glass" style={{ overflow: "hidden" }}>
          <div className="matrix-head" style={{ paddingTop: "1.1rem" }}><div>Ce que nous faisons</div><div>Ce que nous refusons</div></div>
          {fr.transparency.rows.map((r) => (
            <div className="matrix-row" key={r.p}>
              <div className="yes"><Check size={18} /> <span>{r.p}</span></div>
              <div className="no"><X size={18} /> <span>{r.n}</span></div>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- VSL PREVIEW ---------- */
export function VslPreview() {
  return (
    <section className="section" data-testid="vsl-preview-section">
      <div className="container page-narrow">
        <div className="vsl-player">
          <img src={IMG.vslCover} alt="" />
          <div className="vsl-play"><Link to="/vsl" data-testid="vsl-preview-play"><button aria-label="Voir la VSL"><Play size={30} fill="currentColor" /></button></Link></div>
        </div>
        <p className="muted center" style={{ marginTop: "1rem", fontFamily: "var(--font-mono)", fontSize: "0.82rem" }}>Voir le circuit tourner en 9 minutes, sans coupure.</p>
      </div>
    </section>
  );
}

/* ---------- ANGLES ---------- */
export function AnglesOverview() {
  const { data: angles = [] } = useQuery({ queryKey: ["angles"], queryFn: fetchAngles });
  return (
    <section className="section" id="angles" data-testid="angles-section">
      <div className="container">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{fr.angles.eyebrow}</span>
          <h2 className="h2">{fr.angles.title}</h2>
        </Reveal>
        <div className="grid-4">
          {angles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.05}>
              <Link to={`/o/${a.slug}`} className="glass card" style={{ display: "block", height: "100%" }} data-testid={`angle-card-${a.slug}`}>
                <div className="mono" style={{ color: "var(--v-accent-text)", fontSize: "0.8rem", marginBottom: "0.6rem" }}>/o/{a.slug}</div>
                <h3 className="card__title">{a.secteur}</h3>
                <p className="card__desc">{a.hook}</p>
                <span className="chip" style={{ marginTop: "1rem" }}>{fr.angles.cta} <ArrowRight size={13} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="section" id="faq" data-testid="faq-section">
      <div className="container page-narrow">
        <Reveal className="block-head">
          <span className="eyebrow"><span className="sq" />{fr.faq.eyebrow}</span>
          <h2 className="h2">{fr.faq.title}</h2>
        </Reveal>
        <div>
          {fr.faq.items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div className="faq-item" key={it.q}>
                <button className="faq-q" onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-q-${i}`} aria-expanded={isOpen}>
                  {it.q}{isOpen ? <Minus size={20} /> : <Plus size={20} />}
                </button>
                <AnimatePresence initial={false}>
                  <motion.div
                    className="faq-a"
                    initial={false}
                    animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                    transition={{ duration: 0.24 }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="faq-a__inner">{it.a}</div>
                  </motion.div>
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
export function FinalCta() {
  return (
    <section className="section" data-testid="final-cta-section">
      <div className="container">
        <Reveal className="final-banner">
          <span className="eyebrow" style={{ justifyContent: "center", color: "var(--v-accent-bright)" }}><span className="sq" style={{ background: "var(--v-accent-bright)" }} />{fr.finalCta.eyebrow}</span>
          <h2 className="h1" style={{ marginBottom: "1rem" }}>{fr.finalCta.title}</h2>
          <p className="lead" style={{ marginBottom: "2rem" }}>{fr.finalCta.body}</p>
          <Link to="/pilote" className="btn btn--light" data-testid="final-cta-btn">{fr.finalCta.cta} <ArrowRight size={16} /></Link>
        </Reveal>
      </div>
    </section>
  );
}
